import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { channels, channelCategories, type Channel } from '@/mocks/channels';

function ChannelModal({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#111] border border-[#d4af37]/20 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative h-48 overflow-hidden rounded-t-3xl">
          <img alt={channel.name} className="w-full h-full object-cover object-top" src={channel.thumbnail} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
          <div className="absolute bottom-4 left-6 right-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-600 flex-shrink-0">
                <i className="ri-youtube-fill text-white text-xl"></i>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-white">{channel.name}</h2>
                <div className="flex items-center gap-4 text-sm text-white/70">
                  <span><i className="ri-user-line text-[#d4af37] mr-1"></i>{channel.subscribers} subscribers</span>
                  <span><i className="ri-eye-line text-[#d4af37] mr-1"></i>{channel.views} views</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <p className="text-white/70 text-sm leading-relaxed mb-6">{channel.description}</p>

          <h3 className="font-heading text-lg font-bold text-white mb-4">Featured Videos <span className="text-white/40 text-sm font-normal">({channel.videos.length} videos)</span></h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {channel.videos.map((video, i) => (
              <div key={i} className="group cursor-pointer" onClick={() => setActiveVideo(activeVideo === video.embedId + i ? null : video.embedId + i)}>
                <div className="relative rounded-xl overflow-hidden mb-2">
                  {activeVideo === video.embedId + i ? (
                    <iframe
                      className="w-full aspect-video"
                      src={`https://www.youtube.com/embed/${video.embedId}?autoplay=1`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <>
                      <img alt={video.title} className="w-full aspect-video object-cover object-top transition-transform duration-300 group-hover:scale-105" src={video.thumb} />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-600 group-hover:scale-110 transition-transform">
                          <i className="ri-play-fill text-white text-lg"></i>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="text-white text-xs font-medium leading-tight line-clamp-2">{video.title}</div>
                <div className="text-white/40 text-xs mt-0.5">{video.views}</div>
              </div>
            ))}
          </div>

          <a
            href={channel.youtubeUrl}
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105"
          >
            Visit Channel on YouTube <i className="ri-external-link-line"></i>
          </a>
        </div>
      </div>
    </div>
  );
}

export default function YouTubePage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'YouTube Network – Spiritual Bhakti | 23+ Channels, 100M+ Subscribers';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Spiritual Bhakti YouTube Network – 23+ Channels, 100M+ Subscribers',
      url: `${import.meta.env.VITE_SITE_URL || ''}/youtube-network`,
      description: 'India\'s largest Hindi Devotional and folk music YouTube network. 23+ channels including Spiritual Bhakti, Spiritual Bhakti, Bhojpuri Junction with 100M+ subscribers and 100B+ views.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || ''}/` },
          { '@type': 'ListItem', position: 2, name: 'YouTube Network', item: `${import.meta.env.VITE_SITE_URL || ''}/youtube-network` },
        ],
      },
      mainEntity: {
        '@type': 'ItemList',
        name: 'Spiritual Bhakti YouTube Channels',
        numberOfItems: channels.length,
        itemListElement: channels.map((ch, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'WebPage',
            name: ch.name,
            url: ch.youtubeUrl,
          },
        })),
      },
    });
    const existing = document.getElementById('page-jsonld');
    if (existing) existing.remove();
    document.head.appendChild(script);

    const t = setTimeout(() => setVisible(true), 100);
    return () => {
      clearTimeout(t);
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, []);

  const filtered = activeCategory === 'All' ? channels : channels.filter((c) => c.category === activeCategory);

  const totalSubs = '100M+';
  const totalViews = '100B+';

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="Spiritual Bhakti YouTube network"
            className="w-full h-full object-cover object-top"
            src="https://readdy.ai/api/search-image?query=YouTube%20digital%20network%20visualization%2C%20multiple%20screens%20showing%20Indian%20Hindi Devotional%20music%20videos%2C%20dark%20cinematic%20background%2C%20golden%20light%20rays%2C%20technology%20meets%20tradition&width=1920&height=600&seq=yt_hero&orientation=landscape"
          />
          <div className="absolute inset-0 bg-black/85"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#d4af37]"></span>
              <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Digital Empire</span>
              <span className="w-8 h-px bg-[#d4af37]"></span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-4">
              YouTube <span className="text-[#d4af37]">Network</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">
              India's largest Hindi Devotional and folk music YouTube network — 23 channels, 100M+ subscribers, and 100B+ views.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
              {[
                { icon: 'ri-youtube-fill', value: '23+', label: 'YouTube Channels', color: 'text-red-500' },
                { icon: 'ri-user-star-line', value: totalSubs, label: 'Total Subscribers', color: 'text-[#d4af37]' },
                { icon: 'ri-eye-line', value: totalViews, label: 'Total Views', color: 'text-[#d4af37]' },
                { icon: 'ri-award-line', value: '50+', label: 'YouTube Awards', color: 'text-[#d4af37]' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className={`text-3xl md:text-4xl font-bold font-heading ${stat.color}`}>{stat.value}</div>
                  <div className="text-white/60 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 bg-[#080808] border-b border-[#d4af37]/10 sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-1 flex-nowrap">
            {channelCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${activeCategory === cat ? 'btn-gold text-black' : 'border border-[#d4af37]/20 text-white/60 hover:text-white hover:border-[#d4af37]/40'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Channels Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6 text-white/40 text-sm">
            Showing {filtered.length} channels
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((channel, i) => (
              <button
                key={channel.id}
                onClick={() => setSelectedChannel(channel)}
                className={`group glass-card rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:border-[#d4af37]/40 hover:-translate-y-1 text-left ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${Math.min(i * 40, 400)}ms` }}
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    alt={`${channel.name} YouTube channel`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    src={channel.thumbnail}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-red-600">
                    <i className="ri-youtube-fill text-white text-sm"></i>
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2 py-0.5 rounded-full bg-black/60 text-[#d4af37] text-xs border border-[#d4af37]/20">
                      {channel.category}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold text-white mb-2 group-hover:text-[#d4af37] transition-colors leading-tight">{channel.name}</h3>
                  <div className="flex items-center gap-3 text-xs text-white/50 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line text-[#d4af37]"></i>
                      {channel.subscribers}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line text-[#d4af37]"></i>
                      {channel.views}
                    </span>
                  </div>
                  <p className="text-white/50 text-xs leading-relaxed line-clamp-2">{channel.description}</p>
                  <div className="flex items-center gap-1 text-[#d4af37] text-xs font-medium mt-3">
                    View Channel <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Channel Modal */}
      {selectedChannel && (
        <ChannelModal channel={selectedChannel} onClose={() => setSelectedChannel(null)} />
      )}

      {/* Subscribe CTA */}
      <section className="py-20 bg-[#080808]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-600 mx-auto mb-6">
            <i className="ri-youtube-fill text-white text-3xl"></i>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Subscribe to Our <span className="text-[#d4af37]">Channels</span>
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Never miss a new release. Subscribe to Spiritual Bhakti and all our channels for the latest Hindi Devotional and folk music.
          </p>
          <a
            href="https://www.youtube.com/@GangaCassette"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-white whitespace-nowrap bg-red-600 hover:bg-red-700 cursor-pointer transition-all hover:scale-105"
          >
            <i className="ri-youtube-fill"></i> Subscribe on YouTube
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
